import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { 
  Droplets, 
  Phone, 
  Mail, 
  ShoppingCart, 
  Check, 
  Truck, 
  Shield,
  Award,
  ChevronRight,
  Upload,
  FileText,
  ArrowLeft,
  Building2,
  Palette,
  Download,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";

const PRODUCTS = [
  {
    id: "fresh-n-pure",
    name: "Fresh N Pure Purified Water",
    description: "Case of 24 Individual 500 mL (16.9 oz) Bottles",
    fullDescription: "Premium purified drinking water processed through Micron Filtering, Reverse Osmosis, and UV filtering for a fresh, clean taste every time. Bottled locally in Houston, Texas.",
    price: 5.99,
    priceUnit: "per case",
    palletCost: 431.28,
    deliveryHandling: 163.72,
    totalPalletCost: 595.00,
    image: "https://static.prod-images.emergentagent.com/jobs/05f7cb08-c38a-4279-84de-df8cd62808bb/images/0acdc59060ccf5fb6153bb71a98997f77fe1d0efc57bcf8288dc7121139334ee.png",
    logo: "https://customer-assets.emergentagent.com/job_05f7cb08-c38a-4279-84de-df8cd62808bb/artifacts/0xb3bemk_FreshnPure%20Logo%20169%20bottle.jpeg",
    item_number: "SWDWFNP2",
    manufacturer: "Bleu Flux Beverage Co",
    size: "500 mL (16.9 oz)",
    packSize: "24 bottles per case",
    palletSize: "72 cases per pallet",
    moq: "1 pallet (72 cases)",
    type: "branded"
  },
  {
    id: "private-label",
    name: "Premium Custom Branded Water",
    description: "16.9 oz Smooth Cylinder Bottles — Add Your Logo",
    fullDescription: "Premium custom branded purified water bottles. Add your logo and branding to create a professional, personalized water bottle for your business, event, or organization. Same high-quality purified water with your custom label.",
    price: 727.84,
    priceUnit: "per pallet",
    volumePricing: [
      { range: "1-4 pallets", price: 727.84 },
      { range: "5-10 pallets", price: 687.44 },
      { range: "11-19 pallets", price: 645.84 },
      { range: "20+ pallets", price: 595.44 }
    ],
    image: "https://static.prod-images.emergentagent.com/jobs/05f7cb08-c38a-4279-84de-df8cd62808bb/images/3f1b06a37181e922dd925b6a57eab99582b013f566a8632a6e0a3969c22f4a22.png",
    item_number: "BF-PL-169",
    manufacturer: "Bleu Flux Beverage Co",
    size: "16.9 oz (500 mL)",
    packSize: "24 bottles per case",
    palletSize: "72 cases per pallet",
    moq: "1 pallet (72 cases)",
    type: "private-label"
  }
];

export default function BlueFluxLandingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [formData, setFormData] = useState({
    facilityName: "",
    contactName: "",
    email: "",
    phone: "",
    shippingAddress: "",
    poNumber: "",
    poFile: null,
    artworkFile: null,
    paymentTerms: "net30",
    notes: "",
    organizationType: "",
    quantity: "1"
  });

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("facility_name", formData.facilityName);
      fd.append("contact_name", formData.contactName);
      fd.append("email", formData.email);
      fd.append("phone", formData.phone);
      fd.append("shipping_address", formData.shippingAddress);
      fd.append("po_number", formData.poNumber);
      fd.append("payment_terms", formData.paymentTerms);
      fd.append("notes", formData.notes + ` | Qty: ${formData.quantity} pallets | Product: ${selectedProduct.name}`);
      fd.append("item_number", selectedProduct.item_number);
      fd.append("description", selectedProduct.name);
      fd.append("quantity", formData.quantity);
      fd.append("unit_price", selectedProduct.totalPalletCost || selectedProduct.price);
      fd.append("total", ((selectedProduct.totalPalletCost || selectedProduct.price) * parseInt(formData.quantity || 1)).toFixed(2));
      if (formData.poFile) fd.append("po_file", formData.poFile);
      if (formData.artworkFile) fd.append("artwork_file", formData.artworkFile);

      const res = await fetch(`${API}/marketplace/orders/po-order`, { method: "POST", body: fd });
      if (res.ok) {
        setOrderSuccess(true);
        setShowOrderForm(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/hospital/marketplace" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Marketplace
          </Link>
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-sky-500" />
            <span className="font-bold text-slate-800">Bleu Flux</span>
            <span className="text-slate-400 text-sm">Water Distribution</span>
            <Link to="/hospital/blueflux/about" className="ml-4 text-sm text-sky-600 hover:text-sky-700 font-medium">About</Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-sky-600 via-blue-700 to-sky-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full mb-6">
                <Droplets className="w-4 h-4 text-sky-300" />
                <span className="text-sm text-sky-200 font-medium">Bleu Flux Beverage Co — Houston, TX</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" data-testid="blueflux-hero-title">
                Premium Purified Water Distribution
              </h1>
              <p className="text-sky-200 text-lg mb-6 leading-relaxed">
                The evolution of H2O. Our clean water process combines Micron Filtering, Reverse Osmosis, and UV filtering to guarantee a fresh and pure taste every time. Locally sourced, refreshing, and ready for your business.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-3">
                  <p className="text-sky-300 text-xs uppercase tracking-wide">MOQ</p>
                  <p className="text-lg font-bold">1 Pallet</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-3">
                  <p className="text-sky-300 text-xs uppercase tracking-wide">Cases/Pallet</p>
                  <p className="text-lg font-bold">72 Cases</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-3">
                  <p className="text-sky-300 text-xs uppercase tracking-wide">Bottles/Case</p>
                  <p className="text-lg font-bold">24 x 16.9oz</p>
                </div>
              </div>
              <Button 
                size="lg" 
                className="bg-white text-sky-700 hover:bg-sky-50 font-bold text-lg px-8"
                onClick={() => document.getElementById('products')?.scrollIntoView({behavior: 'smooth'})}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                View Products
              </Button>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/05f7cb08-c38a-4279-84de-df8cd62808bb/images/0acdc59060ccf5fb6153bb71a98997f77fe1d0efc57bcf8288dc7121139334ee.png"
                alt="Fresh N Pure Water"
                className="w-full max-w-lg ml-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* About Blue Flux */}
      <div className="bg-slate-50 border-b">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Droplets, title: "Triple Filtered", desc: "Micron, RO & UV purification" },
              { icon: Shield, title: "Quality Assured", desc: "FDA compliant processing" },
              { icon: Truck, title: "Local Distribution", desc: "Houston, TX based" },
              { icon: Award, title: "Private Label", desc: "Custom branding available" }
            ].map((item, i) => (
              <div key={i} className="p-6 bg-white rounded-xl border">
                <div className="w-12 h-12 bg-sky-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="font-semibold text-slate-800">{item.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div id="products" className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-slate-800 mb-2 text-center">Our Products</h2>
        <p className="text-slate-500 text-center mb-10">Select a product to place your order</p>

        <div className="grid md:grid-cols-2 gap-8">
          {PRODUCTS.map((product) => (
            <div 
              key={product.id}
              className={`bg-white rounded-2xl border-2 overflow-hidden transition-all cursor-pointer hover:shadow-lg ${
                selectedProduct?.id === product.id ? "border-sky-500 shadow-lg ring-2 ring-sky-100" : "border-slate-200"
              }`}
              onClick={() => { setSelectedProduct(product); setOrderSuccess(false); }}
              data-testid={`product-card-${product.id}`}
            >
              {/* Product Image */}
              <div className={`h-56 flex items-center justify-center p-6 ${
                product.type === "private-label" ? "bg-gradient-to-br from-slate-100 to-slate-50" : "bg-gradient-to-br from-sky-50 to-blue-50"
              }`}>
                <img src={product.image} alt={product.name} className="h-full object-contain rounded-lg" />
              </div>

              {/* Product Info */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    {product.type === "private-label" && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded mb-2">
                        <Palette className="w-3 h-3" /> Custom Branding
                      </span>
                    )}
                    <h3 className="text-lg font-bold text-slate-800">{product.name}</h3>
                    <p className="text-sm text-slate-500">{product.description}</p>
                  </div>
                </div>

                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold text-sky-600">
                      ${product.price.toFixed(2)}
                      <span className="text-sm text-slate-400 font-normal ml-1">{product.priceUnit}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1">MOQ: {product.moq}</p>
                  </div>
                  <Button 
                    variant={selectedProduct?.id === product.id ? "default" : "outline"}
                    className={selectedProduct?.id === product.id ? "bg-sky-600 hover:bg-sky-700" : ""}
                    onClick={(e) => { e.stopPropagation(); setSelectedProduct(product); setOrderSuccess(false); document.getElementById('order-section')?.scrollIntoView({behavior: 'smooth'}); }}
                    data-testid={`select-${product.id}`}
                  >
                    {selectedProduct?.id === product.id ? <><Check className="w-4 h-4 mr-1" /> Selected</> : "Select"}
                  </Button>
                </div>

                {/* Pallet Cost Breakdown for Fresh N Pure */}
                {product.totalPalletCost && (
                  <div className="mt-4 bg-sky-50 rounded-lg p-3 border border-sky-100">
                    <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Pallet Cost Breakdown</p>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">72 cases x ${product.price.toFixed(2)}</span>
                        <span className="text-slate-700">${product.palletCost.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Delivery & Handling</span>
                        <span className="text-slate-700">${product.deliveryHandling.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between border-t border-sky-200 pt-1.5 mt-1.5">
                        <span className="font-bold text-slate-800">Total per Pallet</span>
                        <span className="font-bold text-sky-700 text-lg">${product.totalPalletCost.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Volume Pricing for Private Label */}
                {product.volumePricing && (
                  <div className="mt-4 bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Volume Pricing (per pallet)</p>
                    <div className="grid grid-cols-4 gap-1 text-center">
                      {product.volumePricing.map((vp, i) => (
                        <div key={i} className="bg-white rounded p-2 border">
                          <p className="text-xs text-slate-500">{vp.range}</p>
                          <p className="text-sm font-bold text-slate-800">${vp.price}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Section */}
      <div id="order-section" className="bg-slate-50 border-t">
        <div className="max-w-3xl mx-auto px-4 py-16">
          {orderSuccess ? (
            <div className="bg-white rounded-2xl border p-8 text-center" data-testid="order-success">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-2">Order Submitted Successfully</h3>
              <p className="text-slate-500 mb-6">We'll review your order and get back to you within 1 business day.</p>
              <Button onClick={() => { setOrderSuccess(false); setSelectedProduct(null); }} variant="outline">Place Another Order</Button>
            </div>
          ) : !selectedProduct ? (
            <div className="text-center py-12">
              <Droplets className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">Select a Product Above</h3>
              <p className="text-slate-400">Choose a product to begin your order</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border shadow-sm p-8">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-16 h-16 object-contain rounded-lg border" />
                <div>
                  <h3 className="text-lg font-bold text-slate-800">{selectedProduct.name}</h3>
                  <p className="text-sm text-slate-500">{selectedProduct.description}</p>
                </div>
              </div>

              <h3 className="text-xl font-bold text-slate-800 mb-4">Place Your Order</h3>

              <form onSubmit={handleSubmitOrder} className="space-y-4">
                {/* Organization Type */}
                <div>
                  <Label>Organization Type *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, organizationType: "business"})}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        formData.organizationType === "business" ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="org-type-business"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-sky-600" />
                        <p className="font-semibold text-slate-800 text-sm">Business / Retailer</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, organizationType: "distributor"})}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        formData.organizationType === "distributor" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="org-type-distributor"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Truck className="w-4 h-4 text-blue-600" />
                        <p className="font-semibold text-slate-800 text-sm">Distributor</p>
                      </div>
                    </button>
                  </div>
                </div>

                {formData.organizationType === "distributor" ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center" data-testid="distributor-contact-cta">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Phone className="w-7 h-7 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Distributor Pricing Available</h3>
                    <p className="text-slate-600 mb-4 text-sm">
                      Contact our sales team for distributor volume pricing and partnership opportunities.
                    </p>
                    <div className="space-y-3">
                      <a href="tel:2814563458" className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold" data-testid="distributor-call-btn">
                        <Phone className="w-5 h-5" />Call (281) 456-3458
                      </a>
                      <a href="mailto:orders@sterilemate.com?subject=Distributor Inquiry - Blue Flux Water" className="flex items-center justify-center gap-2 bg-white border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold" data-testid="distributor-email-btn">
                        <Mail className="w-5 h-5" />orders@sterilemate.com
                      </a>
                    </div>
                  </div>
                ) : formData.organizationType === "business" && (!user || !token) ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center" data-testid="auth-prompt">
                    <Shield className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                    <h4 className="text-lg font-bold text-slate-800 mb-2">Sign In Required</h4>
                    <p className="text-sm text-slate-600 mb-4">Please sign in or create an account to complete your purchase.</p>
                    <div className="flex gap-3 justify-center">
                      <Button 
                        type="button"
                        className="bg-sky-600 hover:bg-sky-700"
                        onClick={() => {
                          sessionStorage.setItem("redirect_after_login", "/hospital/blueflux");
                          navigate("/login");
                        }}
                        data-testid="auth-signin-btn"
                      >
                        Sign In
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={() => {
                          sessionStorage.setItem("redirect_after_login", "/hospital/blueflux");
                          navigate("/register");
                        }}
                        data-testid="auth-register-btn"
                      >
                        Create Account
                      </Button>
                    </div>
                  </div>
                ) : formData.organizationType === "business" ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="facilityName">Company Name *</Label>
                        <Input id="facilityName" required value={formData.facilityName} onChange={(e) => setFormData({...formData, facilityName: e.target.value})} placeholder="Your company" data-testid="input-facility" />
                      </div>
                      <div>
                        <Label htmlFor="contactName">Contact Name *</Label>
                        <Input id="contactName" required value={formData.contactName} onChange={(e) => setFormData({...formData, contactName: e.target.value})} placeholder="Full name" data-testid="input-contact" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input id="email" type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@company.com" data-testid="input-email" />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input id="phone" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" data-testid="input-phone" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="shippingAddress">Shipping Address *</Label>
                      <Input id="shippingAddress" required value={formData.shippingAddress} onChange={(e) => setFormData({...formData, shippingAddress: e.target.value})} placeholder="Full shipping address" data-testid="input-address" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="quantity">Number of Pallets *</Label>
                        <Input id="quantity" type="number" min="1" required value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: e.target.value})} data-testid="input-quantity" />
                      </div>
                      <div>
                        <Label htmlFor="poNumber">PO Number</Label>
                        <Input id="poNumber" value={formData.poNumber} onChange={(e) => setFormData({...formData, poNumber: e.target.value})} placeholder="Optional" data-testid="input-po" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="poFile">Upload Purchase Order (optional)</Label>
                      <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-sky-400 transition-colors" onClick={() => document.getElementById('poFileInput')?.click()}>
                        <input id="poFileInput" type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.png" onChange={(e) => setFormData({...formData, poFile: e.target.files[0]})} />
                        {formData.poFile ? (
                          <div className="flex items-center justify-center gap-2 text-sky-600">
                            <FileText className="w-5 h-5" />
                            <span className="font-medium">{formData.poFile.name}</span>
                          </div>
                        ) : (
                          <div className="text-slate-400">
                            <Upload className="w-6 h-6 mx-auto mb-1" />
                            <p className="text-sm">Click to upload PO document</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Artwork Upload - Private Label Only */}
                    {selectedProduct?.type === "private-label" && (
                      <div data-testid="artwork-upload-section">
                        <Label htmlFor="artworkFile">Upload Your Artwork / Label Design *</Label>
                        <p className="text-xs text-slate-500 mb-2">Upload your logo or label artwork. Accepted formats: JPEG, PNG, PDF, AI, EPS, SVG</p>
                        <div 
                          className={`mt-1 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${formData.artworkFile ? "border-amber-400 bg-amber-50" : "border-slate-300 hover:border-amber-400"}`}
                          onClick={() => document.getElementById('artworkFileInput')?.click()}
                        >
                          <input 
                            id="artworkFileInput" 
                            type="file" 
                            className="hidden" 
                            accept=".jpg,.jpeg,.png,.pdf,.ai,.eps,.svg,.tiff,.tif,.psd,.bmp"
                            onChange={(e) => setFormData({...formData, artworkFile: e.target.files[0]})}
                          />
                          {formData.artworkFile ? (
                            <div className="flex items-center justify-center gap-2 text-amber-700">
                              <Palette className="w-5 h-5" />
                              <span className="font-medium">{formData.artworkFile.name}</span>
                              <button 
                                type="button" 
                                className="ml-2 text-slate-400 hover:text-red-500" 
                                onClick={(e) => { e.stopPropagation(); setFormData({...formData, artworkFile: null}); }}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-slate-400">
                              <Palette className="w-7 h-7 mx-auto mb-2 text-amber-400" />
                              <p className="text-sm font-medium text-slate-600">Click to upload your artwork</p>
                              <p className="text-xs text-slate-400 mt-1">JPG, PNG, PDF, AI, EPS, SVG up to 25MB</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Payment Terms *</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {[{v: "net30", l: "Net 30"}, {v: "upon_receipt", l: "Pay Upon Receipt"}].map(t => (
                          <button key={t.v} type="button"
                            onClick={() => setFormData({...formData, paymentTerms: t.v})}
                            className={`p-3 rounded-lg border-2 text-center transition-colors ${formData.paymentTerms === t.v ? "border-sky-500 bg-sky-50 font-semibold" : "border-slate-200"}`}
                          >{t.l}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="notes">Additional Notes</Label>
                      <textarea id="notes" rows={3} className="w-full mt-1 rounded-lg border border-slate-200 p-3 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Special instructions..." data-testid="input-notes" />
                    </div>

                    {/* Order Summary */}
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 mb-2">Order Summary</h4>
                      {selectedProduct.totalPalletCost ? (
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">{selectedProduct.name}</span>
                            <span className="text-slate-600">{formData.quantity} pallet(s)</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">72 cases x ${selectedProduct.price.toFixed(2)} per pallet</span>
                            <span className="text-slate-700">${(selectedProduct.palletCost * parseInt(formData.quantity || 1)).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Delivery & Handling</span>
                            <span className="text-slate-700">${(selectedProduct.deliveryHandling * parseInt(formData.quantity || 1)).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between border-t border-sky-200 pt-2 mt-2">
                            <span className="font-bold text-slate-800">Total</span>
                            <span className="font-bold text-sky-700 text-lg">${(selectedProduct.totalPalletCost * parseInt(formData.quantity || 1)).toFixed(2)}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{parseInt(formData.quantity || 1) * 72} cases ({parseInt(formData.quantity || 1) * 72 * 24} bottles)</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">{selectedProduct.name} x {formData.quantity} pallet(s)</span>
                            <span className="font-bold text-slate-800">${(selectedProduct.price * parseInt(formData.quantity || 1)).toFixed(2)}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{parseInt(formData.quantity || 1) * 72} cases ({parseInt(formData.quantity || 1) * 72 * 24} bottles)</p>
                        </>
                      )}
                    </div>

                    <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white py-3" disabled={submitting} data-testid="submit-order-btn">
                      {submitting ? "Submitting..." : "Submit Order"}
                    </Button>
                  </>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-slate-500">
                    <p>Please select your organization type above to continue.</p>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Contact Footer */}
      <div className="bg-gradient-to-r from-sky-700 to-blue-800 text-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h3 className="text-xl font-bold mb-2">Questions About Water Distribution?</h3>
          <p className="text-sky-200 mb-6">Contact our sales team for pricing, volume orders, and partnership opportunities.</p>
          <div className="flex items-center justify-center gap-6">
            <a href="tel:2814563458" className="flex items-center gap-2 bg-white/10 px-6 py-3 rounded-lg hover:bg-white/20 transition-colors">
              <Phone className="w-5 h-5" />(281) 456-3458
            </a>
            <a href="mailto:orders@sterilemate.com" className="flex items-center gap-2 bg-white/10 px-6 py-3 rounded-lg hover:bg-white/20 transition-colors">
              <Mail className="w-5 h-5" />orders@sterilemate.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
